import type { ConditionModel, LeadAttribute } from '../../../typings'
import { useFormikContext } from 'formik'
import { useTranslation } from 'react-i18next'
import { useD2DTerritoriesEnabled } from '@user/store/tenantFeatures/hooks/useD2DTerritoriesEnabled'

/**
 * The attributes a condition can be built on.
 *
 * `includeTerritory` is off by default so the label lookup in useValidation -- which has no
 * tenant context and only needs a name for an attribute already chosen -- keeps working
 * unchanged. The dropdown passes it explicitly (WWM-17508): offering Territory to a tenant
 * without the feature would be a condition that can never match anything.
 */
export const attributes = (t, includeTerritory = false) => {
  const attributesArr: { id: LeadAttribute; name: string }[] = [
    { id: 'ZipCode', name: t('postalCode') },
    { id: 'Branch', name: 'Branch' },
    { id: 'Campaign', name: 'Campaign' },
    { id: 'LeadSource', name: 'Lead Source' },
    { id: 'Division', name: 'Division' },
    { id: 'CreatedBy', name: 'Created By' },
    { id: 'LocationType', name: 'Location Type' },
    {
      id: 'State',
      name: t('stateAssignmentRuleLabel'),
    },
    { id: 'TargetPest', name: 'Target Pests' },
    { id: 'LeadType', name: 'Type' },
  ]

  if (includeTerritory) {
    attributesArr.push({ id: 'Territory', name: 'Territory' })
  }

  return attributesArr
}

const matches = [
  { id: 'Any', name: 'Any' },
  { id: 'None', name: 'None' },
]

const initValues: ConditionModel = {
  attribute: '',
  matchType: 'Any',
  assignmentRuleConditionValues: [],
}

export function useData() {
  const { values } = useFormikContext<ConditionModel>()
  const { t } = useTranslation()
  const territoriesEnabled = useD2DTerritoriesEnabled()

  return {
    attributes: attributes(t, territoriesEnabled),
    attribute: values.attribute,
    initValues,
    matches,
  }
}
